/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { getElementData } from '#utils/UNSAFE_visUtils';
import type { RefObject } from 'preact';
import { useRef, useCallback, useState } from 'preact/hooks';

const OVERVIEW_MIN_SIZE = 20;

type Props = {
  /**
   * The width of the overview.
   */
  width: number;

  /**
   * The height of the overview.
   */
  height: number;

  /**
   * The x coordinate of the overview.
   */
  x: number;

  /**
   * The y coordinate of the overview.
   */
  y: number;

  /**
   * The x2 coordinate of the overview window.
   */
  windowX: number;

  /**
   * The y2 coordinate of the overview window.
   */
  windowY: number;

  /**
   * The height of the overview window.
   */
  windowHeight: number;

  /**
   * The width of the overview window.
   */
  windowWidth: number;

  /**
   * The rootRef.
   */
  rootRef: RefObject<HTMLDivElement>;

  /**
   * The callback to call when window dims change.
   */

  onWindowDimsChange?: ({ deltaWidth, deltaX }: { deltaX: number; deltaWidth: number }) => void;
};

export function useOverviewEvents({
  x,
  width,
  windowX,
  windowWidth,
  rootRef,
  windowHeight,
  windowY,
  onWindowDimsChange
}: Props) {
  const pointerDownPointRef = useRef<{ x: number; y: number }>();
  const [lastPoint, setLastPoint] = useState<{ x: number; y: number }>();

  const onPointerDown = useCallback(
    (event: PointerEvent) => {
      pointerDownPointRef.current = { x: event.offsetX, y: event.offsetY };
      (event.target as Element).setPointerCapture(event.pointerId);
      setLastPoint({ x: event.offsetX, y: event.offsetY });
    },
    [pointerDownPointRef]
  );

  const onPointerUp = useCallback(
    (event: PointerEvent) => {
      const { offsetX: clickX, offsetY: clickY } = event;
      const isPointerUpFar =
        Math.abs(pointerDownPointRef.current!.x - clickX) > OVERVIEW_MIN_SIZE / 4;
      pointerDownPointRef.current = undefined;
      setLastPoint(undefined);
      if (
        (clickX <= windowX + windowWidth &&
          clickX >= windowX &&
          clickY <= windowY + windowHeight &&
          clickY >= windowY) ||
        isPointerUpFar
      ) {
        return;
      }

      const newX = Math.min(x + width - windowWidth, Math.max(clickX - windowWidth / 2, x));

      onWindowDimsChange?.({ deltaX: newX - windowX, deltaWidth: 0 });
    },
    [pointerDownPointRef, windowHeight, windowX, windowY, windowWidth, width, x, onWindowDimsChange]
  );

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      const targetType = getElementData(rootRef, event.target as HTMLElement)?.['ojPrivateObject'];
      if (
        !pointerDownPointRef.current ||
        event.target == null ||
        targetType == null ||
        lastPoint == null
      ) {
        return;
      }

      let deltaX = 0;
      let deltaWidth = 0;
      if (targetType === 'window') {
        const delta = event.offsetX - lastPoint.x;
        deltaX = Math.min(x + width - windowWidth, Math.max(x, windowX + delta)) - windowX;
      } else if (
        targetType === 'leftHandle' &&
        event.offsetX <= windowX + windowWidth - OVERVIEW_MIN_SIZE / 2 &&
        event.offsetX > x
      ) {
        deltaX = Math.max(x, windowX + event.offsetX - lastPoint.x) - windowX;
        deltaWidth = -1 * deltaX;
      } else if (
        targetType === 'rightHandle' &&
        event.offsetX >= windowX + OVERVIEW_MIN_SIZE / 2 &&
        event.offsetX < x + width
      ) {
        deltaWidth = Math.min(event.offsetX - lastPoint.x, x + width - windowX);
      }
      setLastPoint({ x: event.offsetX, y: event.offsetY });
      onWindowDimsChange?.({ deltaX, deltaWidth });
      event.stopPropagation();
    },
    [pointerDownPointRef, rootRef, x, lastPoint, width, windowX, windowWidth, onWindowDimsChange]
  );

  return {
    onPointerDown,
    onPointerUp,
    onPointerMove
  };
}
